import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Stack,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { grey, blue } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import UICard, { UICardHeadingLight } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';
import { useResellerContext } from '../utils/contexts';
import { useNotificationContext } from '@/contexts/NotificationContext';
import { suspendCustomer } from '../utils/connectors';
import DataGridSimple, { GridColDef } from '@/components/Shared/DataGridSimple';

export default function List() {
  const navigate = useNavigate();
  const { sidebar, customers, refreshCustomers, loading, error } = useResellerContext();
  const { setNotification } = useNotificationContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  
  // Filter customers client-side
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesSearch = searchTerm === '' || 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === null || customer.status === statusFilter;
      const matchesPlan = planFilter === null || customer.plan === planFilter;
      
      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [customers, searchTerm, statusFilter, planFilter]);

  const statusOptions = ['Active', 'Pending', 'Suspended'];
  const planOptions = ['Enterprise', 'Business'];

  const handleCustomerDetail = (id: string) => {
    navigate(`/resellers/customers/detail/${id}`);
  };

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCustomerId(id);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStatusFilter = (status: string | null) => {
    setStatusFilter(status);
    handleFilterClose();
  };

  const handlePlanFilter = (plan: string | null) => {
    setPlanFilter(plan);
    handleFilterClose();
  };

  const handleAction = (action: string) => {
    handleMenuClose();
    
    if (!selectedCustomerId) return;
    
    switch (action) {
      case 'view':
        navigate(`/resellers/customers/detail/${selectedCustomerId}`);
        break;
      case 'edit':
        navigate(`/resellers/customers/edit/${selectedCustomerId}`);
        break;
      case 'upgrade':
        navigate(`/resellers/customers/upgrade/${selectedCustomerId}`);
        break;
      case 'suspend':
        setSuspendDialogOpen(true);
        break;
      default:
        break;
    }
  };

  const handleSuspendCustomer = async () => {
    if (!selectedCustomerId) return;
    
    try {
      setIsSuspending(true);
      // API call to suspend customer
      const response = await suspendCustomer(selectedCustomerId);
      
      if (response.success) {
        // Refresh the customer list
        await refreshCustomers();
        
        setNotification({
          message: 'Customer has been suspended successfully.',
          severity: 'success',
          open: true
        });
        setSuspendDialogOpen(false);
      } else {
        throw new Error(response.message || 'Failed to suspend customer');
      }
    } catch (error) {
      setNotification({
        message: error instanceof Error ? error.message : 'Failed to suspend customer. Please try again.',
        severity: 'error',
        open: true
      });
    } finally {
      setIsSuspending(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setPlanFilter(null);
    handleFilterClose();
  };

  // Initial data load happens through useResellerContext
  useEffect(() => {
    // If the data hasn't been loaded yet, trigger a refresh
    if (customers.length === 0 && !loading.customers) {
      refreshCustomers();
    }
  }, [customers.length, loading.customers, refreshCustomers]);

  // Define DataGrid columns
  const columns: GridColDef[] = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Customer Name', 
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography fontSize={13}>{params.value}</Typography>
      )
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography fontSize={13}>{params.value}</Typography>
      )
    },
    { 
      field: 'plan', 
      headerName: 'Plan', 
      width: 120,
      renderCell: (params) => (
        <Typography fontSize={13}>{params.value}</Typography>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={
            params.value === 'Active' ? 'success' : 
            params.value === 'Pending' ? 'warning' : 
            'error'
          }
          sx={{ fontSize: 12 }}
        />
      )
    },
    { 
      field: 'revenue', 
      headerName: 'Revenue', 
      width: 120,
      renderCell: (params) => (
        <Typography fontSize={13}>{params.value}</Typography>
      )
    },
    { 
      field: 'products', 
      headerName: 'Products', 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {params.value.map((product: string) => (
            <Chip 
              key={product} 
              label={product} 
              size="small" 
              sx={{ fontSize: 11, mb: 0.5 }}
            />
          ))}
        </Stack>
      )
    },
    { 
      field: 'createTime', 
      headerName: 'Created', 
      width: 140,
      renderCell: (params) => (
        <Typography fontSize={13}>{params.value}</Typography>
      )
    },
    { 
      field: 'actions', 
      headerName: 'Actions', 
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Utils.ActionButton
          text="Manage"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleActionClick(e, params.row.id)}
          sx={{ 
            minWidth: '100px',
            justifyContent: 'space-between',
            fontSize: 13,
            fontWeight: 500
          }}
        />
      )
    },
  ], []);

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Box my="auto">
                <Utils.LayoutUtils.MainHeading
                  title="Customer Management"
                  description="View and manage all your customer accounts"
                />
              </Box>
              <Stack my="auto" direction="row" justifyContent="end">
                <Utils.CreateObjectButton
                  href="/resellers/customers/create"
                  text="Add Customer"
                />
              </Stack>
            </Stack>
            
            {error?.customers && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error.customers} 
                <Button size="small" sx={{ ml: 2 }} onClick={() => refreshCustomers()}>
                  Retry
                </Button>
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UICard gradient>
                  <UICardHeadingLight
                    title="Customer Overview"
                    description="Summary of your customer accounts"
                  >
                    <Utils.FormUtils.RefetchButton 
                      light 
                      refetch={refreshCustomers} 
                    />
                  </UICardHeadingLight>
                  <Box borderRadius={2} bgcolor="white">
                    <Box p={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ 
                            border: `1px solid ${grey[200]}`, 
                            borderRadius: 1, 
                            p: 2, 
                            textAlign: 'center',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography color={grey[600]} fontSize={13} fontWeight={500} gutterBottom>
                              Total Customers
                            </Typography>
                            {loading.customers ? (
                              <CircularProgress size={24} sx={{ mx: 'auto', my: 1 }} />
                            ) : (
                              <Typography fontSize={24} fontWeight={600} color={blue[800]}>
                                {customers.length}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ 
                            border: `1px solid ${grey[200]}`, 
                            borderRadius: 1, 
                            p: 2, 
                            textAlign: 'center',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography color={grey[600]} fontSize={13} fontWeight={500} gutterBottom>
                              Active Customers
                            </Typography>
                            {loading.customers ? (
                              <CircularProgress size={24} sx={{ mx: 'auto', my: 1 }} />
                            ) : (
                              <Typography fontSize={24} fontWeight={600} color="success.main">
                                {customers.filter(c => c.status === 'Active').length}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ 
                            border: `1px solid ${grey[200]}`, 
                            borderRadius: 1, 
                            p: 2, 
                            textAlign: 'center',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography color={grey[600]} fontSize={13} fontWeight={500} gutterBottom>
                              Pending Customers
                            </Typography>
                            {loading.customers ? (
                              <CircularProgress size={24} sx={{ mx: 'auto', my: 1 }} />
                            ) : (
                              <Typography fontSize={24} fontWeight={600} color="warning.main">
                                {customers.filter(c => c.status === 'Pending').length}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ 
                            border: `1px solid ${grey[200]}`, 
                            borderRadius: 1, 
                            p: 2, 
                            textAlign: 'center',
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            <Typography color={grey[600]} fontSize={13} fontWeight={500} gutterBottom>
                              Total Revenue
                            </Typography>
                            {loading.customers ? (
                              <CircularProgress size={24} sx={{ mx: 'auto', my: 1 }} />
                            ) : (
                              <Typography fontSize={24} fontWeight={600} color={blue[700]}>
                                {`$${customers
                                  .reduce((sum, c) => sum + parseInt(c.revenue.replace(/[^0-9]/g, '')), 0)
                                  .toLocaleString()}`}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </UICard>

                <UICard gradient sx={{ mt: 3 }}>
                  <UICardHeadingLight
                    title="Customer List"
                    description="Detailed list of all your customers"
                  />
                  <Box borderRadius={2} bgcolor="white">
                    <Box p={2}>
                      <Stack direction="row" spacing={2} justifyContent="space-between" mb={2} alignItems="center">
                        <Box width="100%">
                          <Utils.ObjectFilter 
                            callback={(value: string) => setSearchTerm(value)} 
                            label="Search customers by name or email" 
                            fullWidth
                          />
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {statusFilter && (
                              <Chip 
                                label={`Status: ${statusFilter}`} 
                                size="small" 
                                onDelete={() => setStatusFilter(null)}
                                sx={{ mr: 1, fontSize: 12 }}
                              />
                            )}
                            {planFilter && (
                              <Chip 
                                label={`Plan: ${planFilter}`} 
                                size="small" 
                                onDelete={() => setPlanFilter(null)}
                                sx={{ mr: 1, fontSize: 12 }}
                              />
                            )}
                          </Box>
                          <Tooltip title="Filter">
                            <IconButton onClick={handleFilterClick} size="small">
                              <FilterListIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>
                      
                      <Box sx={{ 
                        border: `1px solid ${grey[200]}`,
                        borderRadius: 1,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.03)',
                        p: 0 
                      }}>
                        {loading.customers ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <DataGridSimple
                            rows={filteredCustomers}
                            columns={columns}
                            onRowClick={(params: any) => handleCustomerDetail(params.row.id)}
                            loading={loading.customers}
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'name', sort: 'asc' }],
                              },
                            }}
                            sx={{
                              '.MuiDataGrid-cell': {
                                cursor: 'pointer',
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </UICard>
              </Grid>
            </Grid>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        sx={{ '& .MuiPaper-root': { minWidth: 200 } }}
      >
        <Typography fontSize={14} fontWeight={500} px={2} py={1}>Filter by Status</Typography>
        {statusOptions.map(status => (
          <MenuItem key={status} onClick={() => handleStatusFilter(status)}>
            <Typography fontSize={14}>{status}</Typography>
          </MenuItem>
        ))}
        <Typography fontSize={14} fontWeight={500} px={2} py={1} mt={1}>Filter by Plan</Typography>
        {planOptions.map(plan => (
          <MenuItem key={plan} onClick={() => handlePlanFilter(plan)}>
            <Typography fontSize={14}>{plan}</Typography>
          </MenuItem>
        ))}
        <Box sx={{ borderTop: `1px solid ${grey[200]}`, mt: 1, pt: 1 }}>
          <MenuItem onClick={clearFilters}>
            <Typography fontSize={14} color="primary.main">Clear all filters</Typography>
          </MenuItem>
        </Box>
      </Menu>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{ '& .MuiPaper-root': { minWidth: 180 } }}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <Typography fontSize={14}>View Details</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleAction('edit')}>
          <Typography fontSize={14}>Edit Customer</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleAction('upgrade')}>
          <Typography fontSize={14}>Upgrade Plan</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleAction('suspend')} sx={{ color: 'error.main' }}>
          <Typography fontSize={14}>Suspend Customer</Typography>
        </MenuItem>
      </Menu>

      {/* Suspend Customer Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
        aria-labelledby="suspend-dialog-title"
      >
        <DialogTitle id="suspend-dialog-title">
          Suspend Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to suspend this customer? This will immediately disable their access to all services.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setSuspendDialogOpen(false)}
            variant="text"
            sx={{ fontSize: 13, fontWeight: 500, borderRadius: 0 }}
            disabled={isSuspending}
          >
            <Typography fontSize={14} fontWeight={400} textTransform="capitalize">
              Cancel
            </Typography>
          </Button>
          <Utils.AsyncButton
            text="Suspend"
            disabled={isSuspending}
            isLoading={isSuspending}
            callback={handleSuspendCustomer}
          />
        </DialogActions>
      </Dialog>
    </Grid>
  );
} 